import { IKeytipProps } from '@fluentui/react/lib/Keytip';

import { AppButtonProps } from '@1js/acui-button';
import { MenuOpenOnExecuteParameter } from '@1js/acui-common';
import { ControlInMenuRendererFunction, MenuDefinition } from '@1js/acui-menu';

import { ControlRendererFunction } from '../../../controls';
import { RibbonControlProps } from '../../Controls/RibbonControlProps';
import { RibbonScalingStep } from '../../Scaling';
import {
  RibbonControlGroupDefinitionSetBase,
  RibbonControlGroupDefinitionSetMLR,
  RibbonControlGroupDefinitionSetSLR,
  RibbonMode,
  RibbonModeMultiline,
  RibbonModeSingleLine,
} from '../Ribbon/Ribbon.Props';
import { Layout } from '../RibbonControlGroupMLR/layoutTypings';

export interface ScaledRibbonGroupBase {
  type: RibbonMode;
  id: string;
  groupLabel: string;
  fastDisabled?: boolean | (() => boolean | undefined);
}

export interface ScaledRibbonGroupMLR extends ScaledRibbonGroupBase {
  type: RibbonModeMultiline;
  id: string;
  groupLabel: string;
  controls: RibbonControlProps[];
  layout?: Layout;
  groupLauncher?: AppButtonProps;
  hideGroupLabel?: boolean;
  // VSO:4229595 - temporarily disable animations for this control group.
  disableAnimations?: boolean;
}

export interface ScaledRibbonGroupSLR extends ScaledRibbonGroupBase {
  type: RibbonModeSingleLine;
  overflowMenuLauncherDisabled?: boolean;
  overflowMenuLauncherLabel: string;
  onOverflowMenuOpen?: (parameter: MenuOpenOnExecuteParameter) => void;
  onOverflowMenuClose?: (id: string) => void;
  hasOwnOverflow?: boolean;
  shouldAllowAllControlsInOverflow?: boolean;
  topLevelControls: RibbonControlProps[];
  overflowControls: RibbonControlProps[];
  keytipProps?: IKeytipProps;
}

export type ScaledRibbonGroup = ScaledRibbonGroupMLR | ScaledRibbonGroupSLR;

/**
 * Used to maintain the current state of the Ribbon controls
 * after a given set of scaling transformations.
 */
export interface RibbonScalingStateBase {
  id: string;
  /**
   * The definition for each Ribbon control group
   */
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSetBase;

  /**
   * The list of scaling steps to apply to this set of control groups.
   */
  scalingSteps: Readonly<RibbonScalingStep>[];

  /**
   * This is an index into the scaling steps array which denotes the number of scaling steps
   * that have been applied to yield the scaledRibbonGroups. A value of zero denotes that no
   * scaling steps have been applied yet.
   */
  appliedScalingStepCount: number;

  /**
   * The Ribbon supports an initial scaling state which is always applied, regardless of how much
   * real estate is available. This index represents the minimum number of scaling steps to apply
   * in order to provide that behavior.
   */
  minimumScalingStepsToApply: number;

  /**
   * The transformed Ribbon groups after the scaling steps were applied.
   */
  scaledRibbonGroups: ScaledRibbonGroupBase[];

  /**
   * The estimated width of the ribbon content at the given scaling step. When present, this will
   * be used by the resizeGroup to bypass unnecessary renders
   */
  estimatedWidth?: number;
}

/**
 * Used to maintain the current state of the Ribbon controls
 * after a given set of scaling transformations.
 */
export interface RibbonScalingStateSLR extends RibbonScalingStateBase {
  type: 'SingleLine';
  /**
   * The definition for each Ribbon control group
   */
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSetSLR;

  /**
   * The transformed Ribbon groups after the scaling steps were applied.
   */
  scaledRibbonGroups: ScaledRibbonGroupSLR[];

  /**
   * Combined overflow menu to show at the end of Ribbon. It collects all items from scaled ribbon
   * groups that requested its overflow menu to be moved to the end of ribbon
   */
  scaledEndOfRibbonOverflowMenu: MenuDefinition;
}

/**
 * Used to maintain the current state of the Ribbon controls
 * after a given set of scaling transformations.
 */
export interface RibbonScalingStateMLR extends RibbonScalingStateBase {
  type: 'Multiline';

  /**
   * The definition for each Ribbon control group
   */
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSetMLR;

  /**
   * The transformed Ribbon groups after the scaling steps were applied.
   */
  scaledRibbonGroups: ScaledRibbonGroupMLR[];

  /**
   * The transformed Ribbon groups after only the initial scaling steps were applied.
   */
  initialScaledRibbonGroups: ScaledRibbonGroupMLR[];

  /**
   * Render functions for each control. This is required to render a ribbon group within a menu
   */
  controlRendererFunction?: ControlRendererFunction;

  /**
   * Menu render functions for each control. This is required to render a ribbon group within a menu
   */
  controlInMenuRendererFunction?: ControlInMenuRendererFunction;
}

export type RibbonScalingState = RibbonScalingStateSLR | RibbonScalingStateMLR;

/**
 * Data cached on a user's machine to allow for faster ribbon loads in subsequent sessions.
 * This data will be cached for each window mode to allow persisted caching through orientation changes
 * windowMode will be of one of the WindowMode type values, but typescript requires the index to be a string or number
 * This typing is asserted when reading the value from localStorage
 */
export interface CachedRibbonScalingState {
  [windowMode: string]: RibbonScalingData;
}

export interface RibbonScalingData {
  windowWidth: number;
  slrScalingState?: ScalingStepsAppliedDict;
  mlrScalingState?: ScalingStepsAppliedDict;
  version: number;
}

export interface ScalingStepsAppliedDict {
  [tabId: string]: number;
}

/**
 * The EstimatedScalingWidthDict holds a map of the estimated width of the ribbon after applying each scaling step
 */
export interface EstimatedScalingWidthDict {
  [scalingStep: number]: number;
}

export type VirtualRibbonScalingState = VirtualRibbonScalingStateSLR | VirtualRibbonScalingStateMLR;

/**
 * The VirtualRibbonScalingState holds up-to-date information of the layout-impacting properties for ribbon controls that have been
 * modified during scaling estimation. Since scaling steps can build off of each other, you can't always trust the information that's
 * in the DOM. For instance, If you drop the label on the Bold button, then come back later and move it to the overflow, you need to know
 * that this control "currently" exists without a label, even though it still has a label in the DOM.
 */
export interface VirtualRibbonScalingStateSLR {
  controls: ScaledControlsMap;
  groups: ScaledGroupsSLRMap;
  containsSingleOverflow: boolean;
}

export interface VirtualRibbonScalingStateMLR {
  controls: ScaledControlsMap;
  groups: ScaledGroupsMLRMap;
}

interface ScaledControlsMap {
  [controlId: string]: ScaledControlData;
}

/**
 * Control calculations used during QuickScaling and derived from either the DOM or from data known at build-time
 */
interface ScaledControlData {
  fullControlWidth: number;
  labelWidth?: number;
  controlState?: ControlState;
  previewGalleryItemWidths?: number[];
  ghostFlyout?: number;
  ghostFlyoutTextContainer?: number;
}

export enum ControlState {
  PartialGallery /*Represents a preview gallery that has dropped some of its top-level buttons*/,
  FullControl /*Represents a full control in its default state */,
  LabelDropped /*Represents a control that has dropped its label*/,
  MovedToOverflow /*Represents a control that has been moved into an overflow menu*/,
}

interface ScaledGroupsSLRMap {
  [groupId: string]: GroupStateSLR;
}

interface GroupStateSLR {
  containsOverflow: boolean;
  topLevelControlCount: number;
}

interface ControlDimensions {
  componentRoot: number;
  textContainer?: number;
  previewGalleryItems?: number[];
  ghostFlyout?: number;
  ghostFlyoutTextContainer?: number;
}

interface ScaledGroupsMLRMap {
  [groupId: string]: GroupStateMLR;
}

interface GroupStateMLR {
  layoutIndex: number;
  isFlyout: boolean;
  width?: number;
}

/**
 * The dictionary of control dimensions that we collect at build-time and use for faster scaling
 */
export interface BuildTimeControlWidths {
  [controlId: string]: ControlDimensions;
}

/**
 * The options/parameters for the ribbon's getScaleDown functions
 */
export interface GetScaleDownFunctionOptions {
  quickScalingIsEnabled: boolean;
  measureContentVisibly: boolean;
  onError?: (message: string) => void;
  mode?: RibbonMode;
}
